import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import type {
  AssignmentExpenseCreate,
  AssignmentExpenseList,
  PatchedAssignmentExpenseUpdate,
  TimeCardDetail,
} from '../../../../../js/api';
import { AssignmentsService } from '../../../../../js/api';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../../components/dialog';
import { DOCUMENTS_QUERY_KEY } from '../../../components/documents-tab';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/table';
import { TooltippedText } from '../../../components/tooltipped-text';
import {
  formatDate,
  formatDateTo24Hr,
  formDateToApiDateTime,
  parseFormDate,
} from '../../../utils/date';
import { useAudience } from '../../../utils/workflowAccess';
import {
  TIMECARD_EXPENSES_QUERY_KEY,
  TIMECARD_QUERY_KEY,
  EXPENSE_CATEGORIES_QUERY_KEY,
} from './queryKeys';

function formatCurrency(value: string | number | null | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num == null || isNaN(num)) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(num);
}

const ExpenseModal: React.FC<{
  open: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  expense?: AssignmentExpenseList;
  timecardId: string;
  onSuccess: () => void;
}> = ({ open, onClose, mode, expense, timecardId, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [expenseAt, setExpenseAt] = useState('');
  const [comment, setComment] = useState('');

  const { data: allCategories = [] } = useQuery({
    queryKey: [EXPENSE_CATEGORIES_QUERY_KEY],
    queryFn: () => AssignmentsService.assignmentsExpenseCategoryList(),
    staleTime: 5 * 60 * 1000,
  });

  const bonusCategory = useMemo(
    () => allCategories.find((cat) => cat.code === 'BONUS'),
    [allCategories]
  );

  React.useEffect(() => {
    if (open) {
      if (mode === 'edit' && expense) {
        setAmount(expense.amount);
        setComment(expense.comment || '');
      } else {
        setAmount('');
        setExpenseAt(parseFormDate(new Date()) || '');
        setComment('');
      }
    }
  }, [open, mode, expense]);

  const createMutation = useMutation({
    mutationFn: (data: AssignmentExpenseCreate) =>
      AssignmentsService.assignmentsTimecardExpensesCreate({
        id: timecardId,
        requestBody: data,
      }),
    onSuccess: () => {
      toast.success('Expense added');
      onSuccess();
      onClose();
    },
    onError: (error) => handleApiError(error),
  });

  const updateMutation = useMutation({
    mutationFn: (data: PatchedAssignmentExpenseUpdate) =>
      AssignmentsService.assignmentsTimecardExpensePartialUpdate({
        id: expense?.id || '',
        requestBody: data,
      }),
    onSuccess: () => {
      toast.success('Expense updated');
      onSuccess();
      onClose();
    },
    onError: (error) => handleApiError(error),
  });

  const isPending = createMutation.isLoading || updateMutation.isLoading;
  const parsedAmount = parseFloat(amount);
  const isValid = !isNaN(parsedAmount) && parsedAmount >= 0 && !!bonusCategory;

  const handleSubmit = () => {
    if (!isValid) return;
    if (mode === 'add' && bonusCategory) {
      createMutation.mutate({
        expense_category: bonusCategory.id,
        amount,
        expense_at: formDateToApiDateTime(expenseAt),
        comment: comment || undefined,
      });
    } else {
      updateMutation.mutate({
        amount,
        comment: comment || undefined,
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <DialogTitle>{mode === 'add' ? 'Add Bonus' : 'Edit Bonus'}</DialogTitle>
      <DialogBody>
        <div className="tw:space-y-4">
          <div>
            <label
              htmlFor="category_title"
              className="tw:block tw:text-sm tw:font-medium tw:text-zinc-700 tw:mb-1"
            >
              Category
            </label>
            <div id="category_title" className="tw:text-sm tw:text-zinc-600 tw:py-2">
              {bonusCategory?.title || 'Bonus'}
            </div>
          </div>
          <div>
            <label
              htmlFor="amount"
              className="tw:block tw:text-sm tw:font-medium tw:text-zinc-700 tw:mb-1"
            >
              Amount
            </label>
            <input
              id="amount"
              type="number"
              min="0"
              step="0.01"
              className="tw:w-full tw:rounded-lg tw:border tw:border-zinc-300 tw:px-3 tw:py-2 tw:text-sm tw:focus:border-brand-green-1 tw:focus:ring-1 tw:focus:ring-brand-green-1 tw:outline-none"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
            />
          </div>
          {mode === 'add' && (
            <div>
              <label
                htmlFor="expense_at"
                className="tw:block tw:text-sm tw:font-medium tw:text-zinc-700 tw:mb-1"
              >
                Bonus Date
              </label>
              <input
                id="expense_at"
                type="date"
                className="tw:w-full tw:rounded-lg tw:border tw:border-zinc-300 tw:px-3 tw:py-2 tw:text-sm tw:focus:border-brand-green-1 tw:focus:ring-1 tw:focus:ring-brand-green-1 tw:outline-none"
                value={expenseAt}
                onChange={(e) => setExpenseAt(e.target.value)}
              />
            </div>
          )}
          <div>
            <label
              htmlFor="comment"
              className="tw:block tw:text-sm tw:font-medium tw:text-zinc-700 tw:mb-1"
            >
              Comment
            </label>
            <textarea
              id="comment"
              className="tw:w-full tw:rounded-lg tw:border tw:border-zinc-300 tw:px-3 tw:py-2 tw:text-sm tw:placeholder-zinc-400 tw:focus:border-brand-green-1 tw:focus:ring-1 tw:focus:ring-brand-green-1 tw:outline-none"
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Optional comment..."
            />
          </div>
        </div>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button color="brandgreen0" onClick={handleSubmit} disabled={!isValid || isPending}>
          {isPending ? 'Saving...' : mode === 'add' ? 'Add' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ExpensesTab: React.FC<{
  timecard: TimeCardDetail;
  canEdit?: boolean;
  ownerId?: string;
  relatedProviderId?: string;
}> = ({ timecard, canEdit = false }) => {
  const queryClient = useQueryClient();
  const audience = useAudience();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [editingExpense, setEditingExpense] = useState<AssignmentExpenseList | undefined>();

  const { data: allCategories = [] } = useQuery({
    queryKey: [EXPENSE_CATEGORIES_QUERY_KEY],
    queryFn: () => AssignmentsService.assignmentsExpenseCategoryList(),
    staleTime: 5 * 60 * 1000,
  });

  const bonusCategory = useMemo(
    () => allCategories.find((cat) => cat.code === 'BONUS'),
    [allCategories]
  );
  const canManageExpenses =
    canEdit &&
    !!bonusCategory &&
    (bonusCategory.allowed_audience_edit?.includes(audience) ?? false);

  const { data: expensesRaw } = useQuery({
    queryKey: [TIMECARD_EXPENSES_QUERY_KEY, timecard.id],
    queryFn: () => AssignmentsService.assignmentsTimecardExpensesList({ id: timecard.id }),
    enabled: !!timecard.id,
  });

  const expenses: AssignmentExpenseList[] = useMemo(() => {
    if (!expensesRaw) return [];
    if (Array.isArray(expensesRaw)) return expensesRaw;
    if ('results' in expensesRaw) return (expensesRaw as any).results || [];
    return [expensesRaw as AssignmentExpenseList];
  }, [expensesRaw]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => AssignmentsService.assignmentsTimecardExpenseDestroy({ id }),
    onSuccess: () => {
      toast.success('Expense removed');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [TIMECARD_EXPENSES_QUERY_KEY, timecard.id] });
    queryClient.invalidateQueries({ queryKey: [TIMECARD_QUERY_KEY, timecard.id] });
    // Bonus expenses feed the timecard document's totals — the edit may have
    // replaced the letter, so the documents tab must refetch.
    queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY, timecard.id] });
  };

  const handleDelete = (expense: AssignmentExpenseList) => {
    if (window.confirm(`Remove bonus expense of ${formatCurrency(expense.amount)}?`)) {
      deleteMutation.mutate(expense.id);
    }
  };

  const total = expenses.reduce((sum, e) => sum + parseFloat(e.amount || '0'), 0);

  return (
    <div className="tw:space-y-6">
      <section>
        {canManageExpenses && (
          <div className="tw:flex tw:justify-end tw:mb-2">
            <Button
              small
              color="brand-green-outline"
              onClick={() => {
                setEditingExpense(undefined);
                setModalMode('add');
                setModalOpen(true);
              }}
            >
              <i className="fas fa-plus tw:mr-1" />
              Add Bonus
            </Button>
          </div>
        )}

        {expenses.length === 0 ? (
          <div className="tw:rounded-md tw:border tw:border-dashed tw:border-zinc-300 tw:dark:border-zinc-700 tw:p-6 tw:text-center tw:text-sm tw:text-zinc-500">
            No timecard expenses recorded
          </div>
        ) : (
          <Table compact striped>
            <TableHead>
              <TableHeader>
                <span />
              </TableHeader>
              <TableHeader>Date</TableHeader>
              <TableHeader>Category</TableHeader>
              <TableHeader className="tw:!text-right">Amount</TableHeader>
              <TableHeader>Comment</TableHeader>
            </TableHead>
            <TableBody>
              {expenses.map((expense) => {
                const expenseTooltipContent = (
                  <div className="tw:flex tw:flex-col tw:items-start">
                    <div className="tw:text-[12px] tw:font-medium">
                      Created By {expense.created_by_name}
                    </div>
                    <div className="tw:text-[12px] tw:font-medium">
                      {formatDateTo24Hr(expense.created_at)}
                    </div>
                  </div>
                );
                return (
                  <TableRow key={expense.id}>
                    <TableCell>
                      <div className="tw:flex tw:items-center tw:gap-2">
                        {canManageExpenses && (
                          <>
                            <button
                              type="button"
                              className="tw:text-zinc-400 tw:hover:text-brand-green-0 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                              onClick={() => {
                                setEditingExpense(expense);
                                setModalMode('edit');
                                setModalOpen(true);
                              }}
                              title="Edit expense"
                            >
                              <i className="fas fa-pencil tw:text-sm" />
                            </button>
                            <button
                              type="button"
                              className="tw:text-zinc-400 tw:hover:text-stoplight-danger tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
                              onClick={() => handleDelete(expense)}
                              title="Remove expense"
                            >
                              <i className="fas fa-xmark tw:text-sm" />
                            </button>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="tw:whitespace-nowrap">
                      {formatDate(expense.expense_at)}
                    </TableCell>
                    <TableCell>
                      {expense.expense_category.title}{' '}
                      <TooltippedText tooltipContent={expenseTooltipContent}>
                        <i className="far fa-circle-info tw:text-blue-700" />
                      </TooltippedText>
                    </TableCell>
                    <TableCell className="tw:text-right">
                      {formatCurrency(expense.amount)}
                    </TableCell>
                    <TableCell className="tw:text-zinc-500 tw:text-xs">
                      {expense.comment && expense.comment.length > 30 ? (
                        <>
                          {expense.comment.slice(0, 26)}...{' '}
                          <TooltippedText
                            tooltipContent={expense.comment}
                            tooltipClassName="tw:max-w-[300px]"
                          >
                            <span className="tw:underline tw:cursor-default tw:text-blue-700">
                              more
                            </span>
                          </TooltippedText>
                        </>
                      ) : (
                        expense.comment || ''
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell />
                <TableCell />
                <TableCell className="tw:font-semibold">Total</TableCell>
                <TableCell className="tw:text-right tw:font-semibold">
                  {formatCurrency(total)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        )}
      </section>

      {canManageExpenses && (
        <ExpenseModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setEditingExpense(undefined);
          }}
          mode={modalMode}
          expense={editingExpense}
          timecardId={timecard.id}
          onSuccess={invalidate}
        />
      )}
    </div>
  );
};
